/**
 * Draws the derived architectural trim: skirting, cornice, wainscot and door casings.
 *
 * Every piece is a box of the same unit geometry differing only in position and scale,
 * which is exactly what instancing is for. Grouping by colour rather than by role — the
 * design said per role — means the whole house's trim is currently **one** draw call
 * instead of four, because a profile paints all its trim one colour. Grouping by colour
 * keeps that true if a later profile ever paints rooms differently.
 *
 * Deliberately not inside the `RigidBody` in `LevelGeometry`: trim has no collider, so
 * no amount of moulding can change where the player may walk.
 */

import { Instance, Instances } from '@react-three/drei';
import type { TrimPiece } from '../game/profile/buildTrim';
import { boxPosition } from './boxUnits';
import { getGradientMap } from './materials';

type Props = {
  pieces: readonly TrimPiece[];
};

function groupByColor(pieces: readonly TrimPiece[]): Map<string, TrimPiece[]> {
  const groups = new Map<string, TrimPiece[]>();
  for (const piece of pieces) {
    const group = groups.get(piece.color);
    if (group) group.push(piece);
    else groups.set(piece.color, [piece]);
  }
  return groups;
}

export function RoomTrim({ pieces }: Props) {
  if (pieces.length === 0) return null;

  return (
    <>
      {[...groupByColor(pieces)].map(([color, group]) => (
        /* frustumCulled off: an instanced mesh is culled by its base geometry's bounding
           sphere at the origin, not by its instances — see MazeGrounds, where the same
           latent bug made whole hedges vanish at the right viewing angle. */
        <Instances
          key={color}
          frustumCulled={false}
          limit={group.length}
          range={group.length}
          receiveShadow
        >
          <boxGeometry args={[1, 1, 1]} />
          {/* The trim colour is its own; it does not tint per instance. */}
          <meshToonMaterial color={color} gradientMap={getGradientMap()} />
          {group.map((piece, index) => (
            <Instance
              key={`${piece.role}-${index}`}
              position={boxPosition(piece.box)}
              scale={[piece.box.size.x, piece.box.size.y, piece.box.size.z]}
            />
          ))}
        </Instances>
      ))}
    </>
  );
}
